/**
 * useStudioState - Centralized State Hook for Studio
 * 
 * This hook consolidates the ~60 state declarations from Studio.tsx
 * to improve maintainability and enable easier testing.
 */

import { useState, useMemo, useRef } from 'react';
import useLocalStorage from './useLocalStorage';
import { useShotLibrary } from './useShotLibrary';
import { useProject } from './useProject';
import { useCharacterLibrary } from './useCharacterLibrary';
import { useSequenceBins } from './useSequenceBins';
import {
    AppState,
    AppStage,
    Character,
    Dogma,
    GenerateVideoParams,
    ImageFile,
    NanoEditorContext,
    PromptSequence,
    SequenceProgress,
    SequenceVideoData,
    StoryboardPreview,
    VideoFile,
    VideoProvider,
} from '../types';
import { LocalFolder } from '../types/project';
import { ensureShotIds, ShotSpec } from '../types/decoupage';
import { ChainSegment } from '../components/InfiniteChainPanel';
import { RushMedia } from '../types/media';
import { ImageModelProvider } from '../services/imageGenerationService';
import { ProviderKeys } from '../utils/providerKeys';
import {
    VideoSettings,
    DEFAULT_VIDEO_SETTINGS,
    normalizeVideoSettings
} from '../types/videoSettings';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type SequenceMode = 'plan-sequence' | 'decoupage' | 'infinite-chain';
export type ActiveTab = 'conception' | 'editing' | 'export';
export type SettingsTab = 'profile' | 'account' | 'integrations';

export interface StoryboardModalContext {
    segmentIndex: number;
    baseImage: ImageFile;
    currentPrompt: string;
    mode: 'single-select' | 'ordered-select';
}

export interface EditingPromptDetails {
    index: number;
    prompt: string;
    thumbnail?: string;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export const useStudioState = () => {
    // === CORE APP STATE ===
    const [appState, setAppState] = useState<AppState>(AppState.IDLE);
    const [currentStage, setCurrentStage] = useState<AppStage>(AppStage.PROMPTING);
    const [activeTab, setActiveTab] = useState<ActiveTab>('conception');
    const [resetKey, setResetKey] = useState(0);
    const [onboardingKey, setOnboardingKey] = useState(0);
    const [activeSettingsTab, setActiveSettingsTab] = useState<SettingsTab>('profile');

    // === VIDEO STATE ===
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [lastConfig, setLastConfig] = useState<GenerateVideoParams | null>(null);
    const [lastVideoObject, setLastVideoObject] = useState<any | null>(null);
    const [lastVideoBlob, setLastVideoBlob] = useState<Blob | null>(null);
    const [isMediaSaving, setIsMediaSaving] = useState(false);
    const [isCurrentVideoSaved, setIsCurrentVideoSaved] = useState(false);

    // === UI MESSAGES ===
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showStatusMessage, setShowStatusMessage] = useState<string | null>(null);

    // === MODAL VISIBILITY ===
    const [showStudioConfig, setShowStudioConfig] = useState(false);
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
    const [isShotLibraryOpen, setIsShotLibraryOpen] = useState(false);
    const [isProjectManagerOpen, setIsProjectManagerOpen] = useState(false);
    const [isCharacterManagerOpen, setIsCharacterManagerOpen] = useState(false);
    const [isDogmaManagerOpen, setIsDogmaManagerOpen] = useState(false);
    const [isContactSheetOpen, setIsContactSheetOpen] = useState(false);

    // === API KEYS (Persisted) ===
    const [apiKey, setApiKey] = useLocalStorage<string | null>('studio_api_key', null);
    const [ltxApiKey, setLtxApiKey] = useLocalStorage<string | null>(ProviderKeys.FAL_LTX, null);
    const [hasCustomKey, setHasCustomKey] = useState(!!apiKey);

    // === SEQUENCE STATE (Persisted) ===
    const [promptSequence, setPromptSequence] = useLocalStorage<PromptSequence | null>(
        'studio_prompt_sequence',
        null,
    );
    const [activePromptIndex, setActivePromptIndex] = useState<number | null>(null);
    const [sequenceProgress, setSequenceProgress] = useState<SequenceProgress | null>(null);
    const [isRevisingSequence, setIsRevisingSequence] = useState<{ fromIndex: number } | null>(null);
    const [sequenceVideoData, setSequenceVideoData] = useLocalStorage<Record<number, SequenceVideoData>>(
        'studio_sequence_video_data',
        {},
    );
    const [mainPromptConfig, setMainPromptConfig] = useState<GenerateVideoParams | null>(null);

    // === NANO BANANA PRO STATE ===
    const [storyboardModalContext, setStoryboardModalContext] = useState<StoryboardModalContext | null>(null);
    const [nanoEditorContext, setNanoEditorContext] = useState<NanoEditorContext | null>(null);
    const [storyboardByIndex, setStoryboardByIndex] = useLocalStorage<Record<number, StoryboardPreview>>(
        'studio_storyboard',
        {},
    );
    const [frameToEdit, setFrameToEdit] = useState<ImageFile | null>(null);
    // P0.4: Track which keyframe segment is being used as start/end image
    const [activeStartImageIndex, setActiveStartImageIndex] = useState<number | null>(null);
    const [activeEndImageIndex, setActiveEndImageIndex] = useState<number | null>(null);

    // === ASSISTANT STATE ===
    const [originalVideoForExtension, setOriginalVideoForExtension] = useState<VideoFile | null>(null);
    const [isExternalVideoSource, setIsExternalVideoSource] = useState(false);
    const [assistantExtensionContext, setAssistantExtensionContext] = useState<ImageFile | null>(null);
    const [assistantImage, setAssistantImage] = useState<ImageFile | null>(null);
    const [assistantReferenceVideo, setAssistantReferenceVideo] = useState<VideoFile | null>(null);
    const [assistantMotionDescription, setAssistantMotionDescription] = useState<string | null>(null);
    const [isAssistantBusy, setIsAssistantBusy] = useState(false);
    const [assistantMessages, setAssistantMessages] = useLocalStorage<any[]>('studio_assistant_messages', []);

    // === CHARACTERS ===
    const [characterToInject, setCharacterToInject] = useState<Character | null>(null);
    const [mentionedCharacters, setMentionedCharacters] = useState<Character[]>([]);

    // === DOGMA STATE (Persisted) ===
    const [dogmas, setDogmas] = useLocalStorage<Dogma[]>('dogma-library', []);
    const [dogmaFolders, setDogmaFolders] = useLocalStorage<LocalFolder[]>('dogma-folders', []);
    const [activeDogmaId, setActiveDogmaId] = useLocalStorage<string | null>('active-dogma-id', null);
    const [sequenceBoundDogma, setSequenceBoundDogma] = useState<Dogma | null>(null);
    const [pendingDogma, setPendingDogma] = useState<Dogma | null>(null);

    const activeDogma = useMemo(
        () => dogmas.find((d) => d.id === activeDogmaId) ?? null,
        [dogmas, activeDogmaId],
    );

    const createDogmaFolder = (name: string, parentId?: string | null) => {
        const newFolder: LocalFolder = {
            id: new Date().getTime().toString(), // Simple ID gen
            name,
            parentId: parentId || null,
            type: 'dogma',
            createdAt: new Date().toISOString()
        };
        setDogmaFolders(prev => [...prev, newFolder]);
    };

    const deleteDogmaFolder = (folderId: string) => {
        // Move contents to root
        setDogmas(prev => prev.map(d => d.folderId === folderId ? { ...d, folderId: undefined } : d));
        // Delete folder
        setDogmaFolders(prev => prev.filter(f => f.id !== folderId));
    };

    // === DÉCOUPAGE PRO STATE ===
    const [decoupageShots, setDecoupageShots] = useLocalStorage<ShotSpec[]>('decoupage_shots', []);
    const [decoupageFps, setDecoupageFps] = useLocalStorage<number>('decoupage_fps', 25);
    const [selectedDecoupageShot, setSelectedDecoupageShot] = useState<ShotSpec | null>(null);
    const [candidatesByShotId, setCandidatesByShotId] = useLocalStorage<Record<string, ImageFile[]>>(
        'decoupage_candidates',
        {},
    );
    const [shotCandidates, setShotCandidates] = useState<ImageFile[]>([]);
    const [isGeneratingCandidates, setIsGeneratingCandidates] = useState(false);
    const [selectedCandidateIndex, setSelectedCandidateIndex] = useState<number | undefined>(undefined);
    const [rippleEnabled, setRippleEnabled] = useState(true);
    const [decoupageMasterShot, setDecoupageMasterShot] = useState<ImageFile | null>(null);

    // === KEYFRAME STATE ===
    const [autoKeyframesEnabled, setAutoKeyframesEnabled] = useState(true);
    const [isGeneratingKeyframes, setIsGeneratingKeyframes] = useState(false);
    const [generatingKeyframeIndex, setGeneratingKeyframeIndex] = useState<number | null>(null);

    // === MODE SELECTION ===
    const [sequenceMode, setSequenceMode] = useLocalStorage<SequenceMode>('studio_sequence_mode', 'plan-sequence');

    // === INFINITE CHAIN STATE ===
    const [chainSegments, setChainSegments] = useLocalStorage<ChainSegment[]>('studio_chain_segments', []);
    const [isBatchGeneratingChain, setIsBatchGeneratingChain] = useState(false);

    // === PROMPT EDITING ===
    const [editingPromptDetails, setEditingPromptDetails] = useState<EditingPromptDetails | null>(null);

    // === NLE WORKFLOW ===
    const [sourceMedia, setSourceMedia] = useState<RushMedia | null>(null);

    // === SESSION HISTORY ===
    const [historySessions, setHistorySessions] = useState<any[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    // === PROVIDER SELECTION ===
    // === PROVIDER SELECTION ===
    const [selectedImageProvider, setSelectedImageProvider] = useState<ImageModelProvider>('nano-pro');

    // Unified Video Settings (State Source of Truth)
    const [videoSettings, _setVideoSettings] = useState<VideoSettings>(DEFAULT_VIDEO_SETTINGS);

    const setVideoSettings = (newSettings: VideoSettings | ((prev: VideoSettings) => VideoSettings)) => {
        _setVideoSettings(prev => {
            const next = typeof newSettings === 'function' ? newSettings(prev) : newSettings;
            return normalizeVideoSettings(next);
        });
    };

    // === FORM STATE ===
    const [initialFormValues, setInitialFormValues] = useState<GenerateVideoParams | null>(null);
    const [apiKeyError, setApiKeyError] = useState<string | null>(null);

    // === REFS ===
    const abortControllerRef = useRef<AbortController | null>(null);

    // === COMPUTED VALUES ===
    const sortedSequenceHistory = useMemo(() => {
        return Object.entries(sequenceVideoData)
            .map(([k, v]) => ({ idx: Number(k), v }))
            .filter((x) => Number.isFinite(x.idx))
            .sort((a, b) => a.idx - b.idx)
            .map((x) => x.v);
    }, [sequenceVideoData]);

    // === EXTERNAL HOOKS (for convenience, re-exported) ===
    const shotLibrary = useShotLibrary();
    const projectHook = useProject();
    const characterLibrary = useCharacterLibrary();
    const sequenceBins = useSequenceBins();

    // ============================================================================
    // RETURN ALL STATE
    // ============================================================================
    return {
        // Core App
        appState, setAppState,
        currentStage, setCurrentStage,
        activeTab, setActiveTab,
        resetKey, setResetKey,
        onboardingKey, setOnboardingKey,
        activeSettingsTab, setActiveSettingsTab,

        // Video
        videoUrl, setVideoUrl,
        lastConfig, setLastConfig,
        lastVideoObject, setLastVideoObject,
        lastVideoBlob, setLastVideoBlob,
        isMediaSaving, setIsMediaSaving,
        isCurrentVideoSaved, setIsCurrentVideoSaved,

        // Messages
        errorMessage, setErrorMessage,
        showStatusMessage, setShowStatusMessage,

        // Modals
        showStudioConfig, setShowStudioConfig,
        isExportDialogOpen, setIsExportDialogOpen,
        showThemeModal, setShowThemeModal,
        showApiKeyDialog, setShowApiKeyDialog,
        isShotLibraryOpen, setIsShotLibraryOpen,
        isProjectManagerOpen, setIsProjectManagerOpen,
        isCharacterManagerOpen, setIsCharacterManagerOpen,
        isDogmaManagerOpen, setIsDogmaManagerOpen,
        isContactSheetOpen, setIsContactSheetOpen,

        // API Keys
        apiKey, setApiKey,
        ltxApiKey, setLtxApiKey,
        hasCustomKey, setHasCustomKey,

        // Sequence
        promptSequence, setPromptSequence,
        activePromptIndex, setActivePromptIndex,
        sequenceProgress, setSequenceProgress,
        isRevisingSequence, setIsRevisingSequence,
        sequenceVideoData, setSequenceVideoData,
        mainPromptConfig, setMainPromptConfig,
        sortedSequenceHistory,

        // Nano
        storyboardModalContext, setStoryboardModalContext,
        nanoEditorContext, setNanoEditorContext,
        storyboardByIndex, setStoryboardByIndex,
        frameToEdit, setFrameToEdit,
        // P0.4: Active start/end image tracking
        activeStartImageIndex, setActiveStartImageIndex,
        activeEndImageIndex, setActiveEndImageIndex,

        // Assistant
        originalVideoForExtension, setOriginalVideoForExtension,
        isExternalVideoSource, setIsExternalVideoSource,
        assistantExtensionContext, setAssistantExtensionContext,
        assistantImage, setAssistantImage,
        assistantReferenceVideo, setAssistantReferenceVideo,
        assistantMotionDescription, setAssistantMotionDescription,
        isAssistantBusy, setIsAssistantBusy,
        assistantMessages, setAssistantMessages,

        // Characters
        characterToInject, setCharacterToInject,
        mentionedCharacters, setMentionedCharacters,

        // Dogma
        dogmas, setDogmas,
        activeDogmaId, setActiveDogmaId,
        activeDogma,
        sequenceBoundDogma, setSequenceBoundDogma,
        pendingDogma, setPendingDogma,
        dogmaFolders, createDogmaFolder, deleteDogmaFolder,

        // Découpage
        decoupageShots, setDecoupageShots,
        decoupageFps, setDecoupageFps,
        selectedDecoupageShot, setSelectedDecoupageShot,
        candidatesByShotId, setCandidatesByShotId,
        shotCandidates, setShotCandidates,
        isGeneratingCandidates, setIsGeneratingCandidates,
        selectedCandidateIndex, setSelectedCandidateIndex,
        rippleEnabled, setRippleEnabled,
        decoupageMasterShot, setDecoupageMasterShot,

        // Keyframes
        autoKeyframesEnabled, setAutoKeyframesEnabled,
        isGeneratingKeyframes, setIsGeneratingKeyframes,
        generatingKeyframeIndex, setGeneratingKeyframeIndex,

        // Mode
        sequenceMode, setSequenceMode,

        // Infinite Chain
        chainSegments, setChainSegments,
        isBatchGeneratingChain, setIsBatchGeneratingChain,

        // Editing
        editingPromptDetails, setEditingPromptDetails,

        // NLE Workflow
        sourceMedia, setSourceMedia,

        // Session History
        historySessions, setHistorySessions,
        isLoadingHistory, setIsLoadingHistory,

        // Provider Selection
        // Provider Selection
        selectedImageProvider, setSelectedImageProvider,
        videoSettings, setVideoSettings,

        // Form State
        initialFormValues, setInitialFormValues,
        apiKeyError, setApiKeyError,

        // Refs
        abortControllerRef,

        // External hooks (re-exported)
        ...shotLibrary,
        ...projectHook,
        ...characterLibrary,
        ...sequenceBins,
    };
};

// Type for the hook return value
export type StudioState = ReturnType<typeof useStudioState>;
